/**
 * <cart-drawer> — opened by 'cart:open' (dispatched from product-form.js
 * after a successful /cart/add.js, or by clicking the header cart icon),
 * refreshed by 'cart:updated'. All quantity/remove actions hit
 * /cart/change.js then re-fetch this section's markup via the Section
 * Rendering API, so line totals/subtotal are always server-computed.
 *
 * Click handling is fully delegated on the custom element itself (`this`)
 * rather than bound to individual children, because refresh() replaces
 * this.innerHTML wholesale — child-bound listeners would go stale.
 */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;

    this.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('cart:open', () => this.open());
    document.addEventListener('cart:updated', () => this.refresh(true));
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-cart-link]');
      if (opener) {
        event.preventDefault();
        this.open();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-open')) this.close();
    });
  }

  onClick(event) {
    if (event.target.closest('[data-cart-drawer-close]')) {
      this.close();
      return;
    }

    const decrease = event.target.closest('[data-qty-decrease]');
    const increase = event.target.closest('[data-qty-increase]');
    const remove = event.target.closest('[data-cart-item-remove]');

    if (decrease) this.changeQuantity(decrease, -1);
    if (increase) this.changeQuantity(increase, 1);
    if (remove) this.removeItem(remove);
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.querySelector('.cart-drawer__close')?.focus();
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
  }

  async refresh(open = false) {
    try {
      const url = `${window.location.pathname}?section_id=${this.sectionId}`;
      const response = await fetch(url);
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('cart-drawer');
      if (fresh) this.innerHTML = fresh.innerHTML;
      this.updateCartCount();
    } catch (error) {
      // Non-fatal: drawer keeps its last known state.
    }
    if (open) this.open();
  }

  async updateCartCount() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
      });
    } catch (error) {
      // Non-fatal.
    }
  }

  changeQuantity(button, delta) {
    const item = button.closest('[data-cart-item]');
    const valueEl = item.querySelector('[data-qty-value]');
    const newQuantity = Math.max(0, Number(valueEl.textContent) + delta);
    this.updateLine(item.dataset.lineKey, newQuantity);
  }

  removeItem(button) {
    const item = button.closest('[data-cart-item]');
    this.updateLine(item.dataset.lineKey, 0);
  }

  async updateLine(key, quantity) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      await this.refresh();
    } catch (error) {
      // Non-fatal.
    }
  }
}

customElements.define('cart-drawer', CartDrawer);
